/**
 * Shared styled Excel export helper (ExcelJS — already a project dependency).
 * Produces: report title + filter/detail lines, bordered header row, bordered
 * data rows with proper alignment (numeric right-aligned + formatted, text
 * left-aligned), a bold total row for numeric columns, and auto-fit column
 * widths — instead of a bare, unstyled `json_to_sheet` dump.
 */

const THIN_BORDER = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

/**
 * @param {Object} opts
 * @param {string} opts.fileName - e.g. 'ItemFullReport_2026-07-13.xlsx'
 * @param {string} [opts.sheetName] - defaults to a trimmed version of the title
 * @param {string} opts.title - report title, shown merged across all columns
 * @param {string[]} [opts.filterLines] - e.g. ['Period: 01-07-2026 to 05-07-2026', 'Branch: All Branches']
 * @param {Array<{key:string, header:string, getValue:(row:any, index:number)=>any, numeric?:boolean, skipSum?:boolean}>} opts.columns
 * @param {any[]} opts.rows - raw row objects; `getValue` extracts each column's value per row
 * @param {string} [opts.footerLabel] - label shown in the first column of the total row (default 'Total')
 * @param {boolean} [opts.showFooterTotal] - set false to omit the total row entirely (e.g. role-gated totals) (default true)
 */
export const exportStyledExcel = async ({ fileName, sheetName, title, filterLines = [], columns, rows = [], footerLabel = 'Total', showFooterTotal = true }) => {
    const ExcelJSModule = await import('exceljs');
    const ExcelJS = ExcelJSModule.default || ExcelJSModule;
    const { saveAs } = await import('file-saver');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet((sheetName || title || 'Report').slice(0, 31));
    const colCount = columns.length;

    if (title) {
        const titleRow = worksheet.addRow([title]);
        worksheet.mergeCells(titleRow.number, 1, titleRow.number, colCount);
        titleRow.font = { bold: true, size: 14 };
    }

    filterLines.filter(Boolean).forEach((line) => {
        const row = worksheet.addRow([line]);
        worksheet.mergeCells(row.number, 1, row.number, colCount);
        row.font = { size: 10, color: { argb: 'FF666666' } };
    });

    if (title || filterLines.length) worksheet.addRow([]);

    const headerRow = worksheet.addRow(columns.map((c) => c.header));
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.eachCell((cell) => {
        cell.border = THIN_BORDER;
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A3AA7' } };
    });

    const totals = new Array(colCount).fill(0);
    const hasTotal = new Array(colCount).fill(false);

    rows.forEach((row, rowIndex) => {
        const values = columns.map((col, idx) => {
            const raw = col.getValue(row, rowIndex);
            if (col.numeric) {
                const n = typeof raw === 'number' ? raw : parseFloat(raw || 0) || 0;
                if (!col.skipSum) {
                    totals[idx] += n;
                    hasTotal[idx] = true;
                }
                return n;
            }
            return raw ?? '-';
        });
        const r = worksheet.addRow(values);
        r.eachCell((cell, colNumber) => {
            cell.border = THIN_BORDER;
            const col = columns[colNumber - 1];
            if (col.numeric) {
                cell.numFmt = '#,##0.00';
                cell.alignment = { horizontal: 'right' };
            } else {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
        });
    });

    if (showFooterTotal && rows.length && hasTotal.some(Boolean)) {
        const footerValues = columns.map((col, idx) => (idx === 0 ? footerLabel : hasTotal[idx] ? totals[idx] : ''));
        const footerRow = worksheet.addRow(footerValues);
        footerRow.font = { bold: true };
        footerRow.eachCell((cell, colNumber) => {
            const col = columns[colNumber - 1];
            cell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
            if (col.numeric) {
                cell.numFmt = '#,##0.00';
                cell.alignment = { horizontal: 'right' };
            }
        });
    }

    // Only measure from the header row down — the merged title/filter rows above it
    // span every column with the same long text, which would otherwise force every
    // column to that same (wrong) width.
    worksheet.columns.forEach((column, idx) => {
        let maxLength = columns[idx]?.header?.length || 10;
        for (let rowNum = headerRow.number; rowNum <= worksheet.rowCount; rowNum++) {
            const cell = worksheet.getRow(rowNum).getCell(idx + 1);
            const len = cell.value != null ? String(cell.value).length : 0;
            if (len > maxLength) maxLength = len;
        }
        column.width = Math.min(Math.max(maxLength + 2, 10), 40);
    });

    worksheet.views = [{ state: 'frozen', ySplit: headerRow.number }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
};

/**
 * Export multiple sheets in one workbook (e.g. Top Selling / Bottom Selling).
 * Each entry in `sheets` takes the same shape as exportStyledExcel minus fileName.
 */
export const exportStyledExcelMultiSheet = async ({ fileName, sheets }) => {
    const ExcelJSModule = await import('exceljs');
    const ExcelJS = ExcelJSModule.default || ExcelJSModule;
    const { saveAs } = await import('file-saver');

    const workbook = new ExcelJS.Workbook();

    sheets.forEach(({ sheetName, title, filterLines = [], columns, rows = [], footerLabel = 'Total' }) => {
        const worksheet = workbook.addWorksheet((sheetName || title || 'Sheet').slice(0, 31));
        const colCount = columns.length;

        if (title) {
            const titleRow = worksheet.addRow([title]);
            worksheet.mergeCells(titleRow.number, 1, titleRow.number, colCount);
            titleRow.font = { bold: true, size: 14 };
        }

        filterLines.filter(Boolean).forEach((line) => {
            const row = worksheet.addRow([line]);
            worksheet.mergeCells(row.number, 1, row.number, colCount);
            row.font = { size: 10, color: { argb: 'FF666666' } };
        });

        if (title || filterLines.length) worksheet.addRow([]);

        const headerRow = worksheet.addRow(columns.map((c) => c.header));
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.eachCell((cell) => {
            cell.border = THIN_BORDER;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A3AA7' } };
        });

        const totals = new Array(colCount).fill(0);
        const hasTotal = new Array(colCount).fill(false);

        rows.forEach((row) => {
            const values = columns.map((col, idx) => {
                const raw = col.getValue(row);
                if (col.numeric) {
                    const n = typeof raw === 'number' ? raw : parseFloat(raw || 0) || 0;
                    if (!col.skipSum) {
                        totals[idx] += n;
                        hasTotal[idx] = true;
                    }
                    return n;
                }
                return raw ?? '-';
            });
            const r = worksheet.addRow(values);
            r.eachCell((cell, colNumber) => {
                cell.border = THIN_BORDER;
                const col = columns[colNumber - 1];
                if (col.numeric) {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                }
            });
        });

        if (rows.length && hasTotal.some(Boolean)) {
            const footerValues = columns.map((col, idx) => (idx === 0 ? footerLabel : hasTotal[idx] ? totals[idx] : ''));
            const footerRow = worksheet.addRow(footerValues);
            footerRow.font = { bold: true };
            footerRow.eachCell((cell, colNumber) => {
                const col = columns[colNumber - 1];
                cell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
                if (col.numeric) {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                }
            });
        }

        worksheet.columns.forEach((column, idx) => {
            let maxLength = columns[idx]?.header?.length || 10;
            for (let rowNum = headerRow.number; rowNum <= worksheet.rowCount; rowNum++) {
                const cell = worksheet.getRow(rowNum).getCell(idx + 1);
                const len = cell.value != null ? String(cell.value).length : 0;
                if (len > maxLength) maxLength = len;
            }
            column.width = Math.min(Math.max(maxLength + 2, 10), 40);
        });

        worksheet.views = [{ state: 'frozen', ySplit: headerRow.number }];
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
};

/** Build the standard "Period: ... " / "Branch: ..." filter lines used across report exports. */
export const buildReportFilterLines = ({ fromDate, toDate, selectedBranch }) => {
    const lines = [];
    if (fromDate && toDate) {
        const fmt = (d) => (typeof d === 'object' && d?.toLocaleDateString ? d.toLocaleDateString('en-IN') : String(d));
        lines.push(`Period: ${fmt(fromDate)} to ${fmt(toDate)}`);
    }
    const branchName = selectedBranch && (selectedBranch.branch_name || selectedBranch);
    lines.push(`Branch: ${branchName || 'All Branches'}`);
    lines.push(`Generated On: ${new Date().toLocaleString('en-IN')}`);
    return lines;
};
